/**
 * NotificationBadge - Tiny overlay pill that conveys an unread count or a
 * simple dot indicator. Typically positioned on top of an icon host.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type NotificationBadgeMode = 'count' | 'dot';

export interface NotificationBadgeOptions extends BaseUIComponentOptions {
  /** Display mode: numeric pill or solid dot. */
  mode?: NotificationBadgeMode;
  /** Current count (only used in `count` mode). */
  count?: number;
  /** Cap for count display; values above show as `${maxCount}+`. */
  maxCount?: number;
}

export class NotificationBadge extends BaseUIComponent<NotificationBadgeOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Notification Badge',
    category: 'feedback',
    summary: 'Tiny overlay pill showing an unread count or a solid dot indicator.',
    useCases: ['unread count', 'inbox badge', 'alert dot', 'notification overlay', 'icon badge'],
    optionsType: 'NotificationBadgeOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/notification-badge.html',
      styles: '@engine/assets/ui/styles/notification-badge.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: NotificationBadge.metadata.assetPaths.template,
      stylesPath: NotificationBadge.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<NotificationBadgeOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      mode: 'count',
      count: 0,
      maxCount: 99,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      mode: this.options.mode,
      label: this.formatLabel(this.options.count),
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-notification-badge') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-notification-badge-label]') as HTMLElement | null;
  }

  /** Update the numeric count shown in `count` mode. */
  public setCount(count: number): void {
    this.options.count = count;
    if (this.labelElement) this.labelElement.textContent = this.formatLabel(count);
  }

  /** Swap between `count` and `dot` modes. */
  public setMode(mode: NotificationBadgeMode): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`mode-${this.options.mode}`);
    this.options.mode = mode;
    this.rootElement.classList.add(`mode-${mode}`);
    this.rootElement.dataset.mode = mode;
  }

  /** Set the cap used to format counts above the threshold. */
  public setMaxCount(maxCount: number): void {
    this.options.maxCount = maxCount;
    if (this.labelElement) this.labelElement.textContent = this.formatLabel(this.options.count);
  }

  private formatLabel(count: number): string {
    if (count > this.options.maxCount) return `${this.options.maxCount}+`;
    return String(Math.max(0, Math.floor(count)));
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.labelElement = null;
  }
}
